'use client';
import { isDefined, sleep } from '@capsicum/common';
import { useAtomValue, useSetAtom } from 'jotai';
import { type FC, useCallback, useMemo, useState } from 'react';
import { match } from 'ts-pattern';

import { useWindowSize } from '../../../hooks/helpers/useWindowSize';
import { ModalService } from '../../../services/alerts';

import { DesktopModalWrapper } from './DesktopModalWrapper';
import { MobileModalWrapper } from './MobileModalWrapper';
import { IsModalDraggableAtom } from './ModalDraggableAtom';

export const ModalWrapper: FC<{}> = () => {
	// --- State Values ---
	const [justClosed, setJustClosed] = useState<boolean>(false);
	const setIsDraggable = useSetAtom(IsModalDraggableAtom);

	// --- Derived Values ---
	const modals = useAtomValue(ModalService.modalStack);
	const { size } = useWindowSize();

	const [modalId, modal] = useMemo(() => {
		const [head] = Array.from(modals.entries());

		return head ?? [];
	}, [modals]);

	const inView = useMemo(() => isDefined(modal) || justClosed, [modal, justClosed]);

	const closeModal = useCallback(
		async (shouldInvokeOnDismiss = true) => {
			if (!modalId) {
				return Promise.resolve();
			}

			setJustClosed(true);
			setIsDraggable(true);
			modal?.onDismiss?.(shouldInvokeOnDismiss);
			await sleep(300);

			setJustClosed(false);
		},
		[modalId, modal]
	);

	const { renderComponent: Component, renderFooter: FooterComponent } = modal ?? {};

	return modal?.renderComponent
		? match(size)
				.with('desktop', () => (
					<DesktopModalWrapper
						background={modal.background}
						child={Component ? <Component close={closeModal} /> : null}
						closeIconColor={modal.closeIconColor}
						closeModal={closeModal}
						detached={modal.detached}
						footer={FooterComponent ? <FooterComponent close={closeModal} /> : null}
						fullHeight={modal.fullHeight}
						inView={inView}
						isClosable={modal.isClosable}
						justClosed={justClosed}
						size={modal.size}
						testID={modal.testID}
					/>
				))
				.with('tablet', () => (
					<MobileModalWrapper
						background={modal.background}
						child={Component ? <Component close={closeModal} /> : null}
						closeIconColor={modal.closeIconColor}
						closeModal={closeModal}
						detached={modal.detached}
						footer={FooterComponent ? <FooterComponent close={closeModal} /> : null}
						fullHeight={modal.fullHeight}
						inView={inView}
						isClosable={modal.isClosable}
						isDraggable={modal.isDraggable}
						justClosed={justClosed}
						testID={modal.testID}
					/>
				))
				.with('mobile', () => (
					<MobileModalWrapper
						background={modal.background}
						child={Component ? <Component close={closeModal} /> : null}
						closeIconColor={modal.closeIconColor}
						closeModal={closeModal}
						detached={modal.detached}
						footer={FooterComponent ? <FooterComponent close={closeModal} /> : null}
						fullHeight={modal.fullHeight}
						inView={inView}
						isClosable={modal.isClosable}
						isDraggable={modal.isDraggable}
						justClosed={justClosed}
						testID={modal.testID}
					/>
				))
				.otherwise(() => null)
		: null;
};
